import { useState } from 'react';
import { formatEta, t, formatPercent, type ResearchState } from '@galaxis/shared';
import { AREA_META, fmtDuration, num, type AreaClient, type TechClient } from './research-format.ts';
import { TechCard } from './TechCard.tsx';

interface Props {
  area: AreaClient;
  state: ResearchState;
  live: { metal: number; energy: number; metalCapacity: number | null } | null;
  now: number;
  busy: boolean;
  onStart: (t: TechClient) => void;
  onOpen: (t: TechClient) => void;
  onCancel: (techId: string, queued: boolean) => void;
}

const BTN_GHOST = 'min-h-11 px-3 rounded border border-slate-600 text-xs text-slate-300 hover:border-slate-400 disabled:opacity-40';

/** One research area: its places (active projects), queue, RP, the hand of cards, the spine and the tree. */
export function AreaPanel({ area, state, live, now, busy, onStart, onOpen, onCancel }: Props) {
  const meta = AREA_META[area.area];
  const techs = state.techs.filter(t => t.area === area.area && !t.discoveryOnly);
  const byId = new Map(state.techs.map(t => [t.id, t]));
  const hand = area.hand.cards.map(id => byId.get(id)).filter((t): t is TechClient => !!t && t.status !== 'done');
  const spine = techs.filter(t => t.kind === 'spine' && t.status === 'available').sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name, 'hu'));
  const freePlaces = Math.max(0, area.slots.total - area.slots.used);
  const [openTiers, setOpenTiers] = useState<Record<number, boolean>>({});

  return (
    <section className={`rounded-xl border ${meta.ring} bg-slate-900/40 p-3 space-y-3`} aria-label={meta.label} data-testid={`area-${area.area}`}>
      <header>
        <h2 className={`text-base font-semibold ${meta.tone}`}>{meta.icon} {meta.label}</h2>
        <p className="text-[11px] text-slate-500">{meta.blurb}</p>
        <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
          <div><dt className="text-slate-500">RP</dt><dd className="font-mono text-slate-100" data-testid={`area-rp-${area.area}`}>{Math.floor(area.rp.balance)} <span className="text-accent">+{num(area.rp.perHour)}/ó</span></dd></div>
          <div><dt className="text-slate-500">Helyek</dt><dd className="font-mono text-slate-100" data-testid={`area-slots-${area.area}`}>{area.slots.used} / {area.slots.total}</dd></div>
          <div><dt className="text-slate-500">Kész</dt><dd className="font-mono text-slate-100" title={`Gerinc: ${area.progress.spineDone} / ${area.progress.spineTotal}`}>{area.progress.done} / {area.progress.total}</dd></div>
        </dl>
        <ul className="mt-1 text-[11px] text-slate-400" data-testid={`rp-sources-${area.area}`}>
          {area.rp.sources.map(src => (
            <li key={src.label} className="flex justify-between gap-2" data-kind={src.kind}
              title={t(`research.rp.tip.${src.kind}`)}>
              <span>{src.kind === 'lab' ? '🔬' : src.kind === 'leader' ? '🧑‍🔬' : src.kind === 'tech' ? '⚙️' : '🏠'} {src.label.startsWith('research.') ? t(src.label, { ...(src.params ?? {}), percent: formatPercent(Number(src.params?.['bonus'] ?? 0)) }) : src.label}</span><span className="font-mono text-slate-300">+{num(src.perHour)}/ó</span>
            </li>
          ))}
          {(area.rp.surveyData ?? 0) > 0 && <li className="flex justify-between gap-2" data-kind="survey" title={t('research.rp.tip.survey')}><span>🛰️ {t('research.rp.surveyData')}</span><span className="font-mono text-slate-300">+{area.rp.surveyData} RP</span></li>}
        </ul>
        <p className="mt-1 text-[11px] text-slate-500" data-testid={`area-tier-${area.area}`}>
          Nyitott szint: {area.tiers.open}.{area.tiers.open < 5 && <> · A következő szinthez még {Math.max(0, area.tiers.gate - (area.tiers.done[String(area.tiers.open)] ?? 0))} kutatás kell a {area.tiers.open}. szintből</>}
        </p>
      </header>

      {/* Places */}
      <div className="space-y-2" data-testid={`places-${area.area}`}>
        {area.active.map(a => {
          const t = byId.get(a.techId);
          const total = Math.max(1, new Date(a.completesAt).getTime() - new Date(a.startedAt).getTime());
          const pct = Math.min(100, Math.max(0, 100 * (now - new Date(a.startedAt).getTime()) / total));
          const left = new Date(a.completesAt).getTime() - now;
          return (
            <div key={a.techId} className="sci-fi-panel rounded-lg p-3 border border-accent/40" data-testid="current-research" data-tech={a.techId}>
              <div className="flex justify-between items-center gap-2 text-sm">
                <span className="text-slate-100"><b>{t?.name ?? a.techId}</b></span>
                <span className="font-mono text-accent text-xs" data-testid="research-eta">{left <= 0 ? 'kész' : formatEta(a.completesAt, now)}</span>
              </div>
              <div className="mt-2 h-2 rounded bg-slate-800 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-label={`${t?.name ?? a.techId} haladása`}>
                <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-2 flex justify-between items-center">
                <span className="text-[11px] text-slate-500">{Math.round(pct)}% · hátra: {fmtDuration(left)}</span>
                <button type="button" className={BTN_GHOST} disabled={busy} data-testid={`cancel-${a.techId}`}
                  title={a.refundable ? 'A kifizetett költség fele visszajár' : 'Nincs visszatérítés'}
                  onClick={() => onCancel(a.techId, false)}>
                  Megszakítás{a.refundable ? ' (50% vissza)' : ''}
                </button>
              </div>
            </div>
          );
        })}
        {Array.from({ length: freePlaces }, (_, i) => (
          <div key={i} className="rounded-lg border border-dashed border-slate-700 p-3 text-xs text-slate-500" data-testid={`free-place-${area.area}`}>
            Szabad kutatási hely — válassz az alábbi lehetőségek vagy a gerinc közül.
          </div>
        ))}
      </div>

      {area.queue.length > 0 && (
        <div data-testid={`queue-${area.area}`}>
          <h3 className="text-xs font-semibold text-slate-400 mb-1">Várólista ({area.queue.length}/{area.queueLimit})</h3>
          <ol className="space-y-1">
            {area.queue.map(q => (
              <li key={q.techId} className="flex items-center justify-between gap-2 text-xs" data-testid={`queued-${q.techId}`}>
                <span className="text-slate-200">{q.position}. {byId.get(q.techId)?.name ?? q.techId}
                  {q.blockedBy && <span className="block text-[11px] text-slate-500">Vár: {q.blockedBy}</span>}
                </span>
                <button type="button" className={BTN_GHOST} disabled={busy} onClick={() => onCancel(q.techId, true)}>Törlés</button>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* The hand */}
      <div data-testid={`hand-${area.area}`} data-draw={area.hand.drawSeq}>
        <h3 className="text-sm font-semibold text-slate-300">Lehetőségek <span className="text-[11px] font-normal text-slate-500">({hand.length}/{area.hand.size} kártya)</span></h3>
        <p className="text-[11px] text-slate-500 mb-1">Új lehetőségek érkeznek, ha ebben a területben egy kutatás befejeződik. A ritka kártya {area.hand.pityAt} kéz után garantált (most: {Math.min(area.hand.pity, area.hand.pityAt)}/{area.hand.pityAt}).</p>
        {hand.length === 0
          ? <p className="text-xs text-slate-500" data-testid={`hand-empty-${area.area}`}>Most nincs kártya: a gerinc technológiái továbbra is kutathatók.</p>
          : <ul className="space-y-2">{hand.map(t => <TechCard key={t.id} variant="card" tech={t} area={area} state={state} live={live} busy={busy} onStart={onStart} onOpen={onOpen} />)}</ul>}
      </div>

      {/* The spine */}
      <div data-testid={`spine-${area.area}`}>
        <h3 className="text-sm font-semibold text-slate-300">Elérhető gerinc <span className="text-[11px] font-normal text-slate-500">({spine.length})</span></h3>
        {spine.length === 0
          ? <p className="text-xs text-slate-500">Most nincs elérhető gerinc-technológia: a következő szint kapuja vagy egy előfeltétel még hiányzik.</p>
          : <ul className="space-y-2 mt-1">{spine.map(t => <TechCard key={t.id} variant="row" tech={t} area={area} state={state} live={live} busy={busy} onStart={onStart} onOpen={onOpen} />)}</ul>}
      </div>

      {/* The whole tree of the area, by tier */}
      <div data-testid={`tree-${area.area}`}>
        <h3 className="text-sm font-semibold text-slate-300 mb-1">Technológia-fa</h3>
        {[1, 2, 3, 4, 5].map(tier => {
          const list = techs.filter(t => t.tier === tier).sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'spine' ? -1 : 1) || a.name.localeCompare(b.name, 'hu'));
          const done = list.filter(t => t.status === 'done').length;
          const open = openTiers[tier] ?? false;
          const tierOpen = tier <= area.tiers.open;
          return (
            <div key={tier} className="border-t border-slate-800">
              <button type="button" className="w-full min-h-11 flex items-center justify-between text-left text-xs text-slate-300"
                aria-expanded={open} aria-controls={`tier-${area.area}-${tier}`} data-testid={`tier-toggle-${area.area}-${tier}`}
                onClick={() => setOpenTiers(o => ({ ...o, [tier]: !open }))}>
                <span>{open ? '▾' : '▸'} {tier}. szint {tierOpen ? '' : '🔒'}</span>
                <span className="font-mono text-slate-500">{done} / {list.length}</span>
              </button>
              {open && (
                <ul id={`tier-${area.area}-${tier}`} className="space-y-2 pb-2">
                  {list.map(t => <TechCard key={t.id} variant="row" tech={t} area={area} state={state} live={live} busy={busy} onStart={onStart} onOpen={onOpen} />)}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
